import React, { useState, useMemo, useEffect } from "react";
import {
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Box,
  useMediaQuery,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Tooltip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import LogoutIcon from "@mui/icons-material/Logout";
import { LayoutDashboard, CirclePercent, Ticket, Calendar, Settings, Presentation, HandCoins, ClipboardCheck, Bell, ChartNoAxesCombined } from "lucide-react";
import GroupIcon from "@mui/icons-material/Group";
import { HealthiconsITrainingClass } from "./Healthicontraining";
import { HugeiconsStudents } from "./HugeiconsStudents";
import { useQuery, useApolloClient } from "@apollo/client/react";
import { ME } from "../graphql/query";
import { useSession } from "../context/SessionContext";
import { useNavigate } from "react-router-dom";
import ConfirmDialog from "./confirmdialogbox";
import themestyle from "../theme";
import logo from "../assets/united-grooves-logo.png";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import Badge from "@mui/material/Badge";
import NotificationDropdown from "./NotificationDropdown";
import CartDrawer from "./CartDrawer";
import { useCart } from "../context/CartContext";

const Logo = "/logo.png";

export const LEFT_RAIL_WIDTH = 88;
export const HEADER_HEIGHT = 72;
const HEADER_HEIGHT_MOBILE = 56;

const Header: React.FC = () => {
  const theme = useTheme();
  const isPhoneLike = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const apollo = useApolloClient();
  const { logout } = useSession();

  const { data }: any = useQuery(ME, {
    fetchPolicy: "network-only",
  });

  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [navAnchorEl, setNavAnchorEl] = useState<null | HTMLElement>(null);
  const { totalCount, isCartOpen, setCartOpen } = useCart();

  const menuOpen = Boolean(anchorEl);
  const navMenuOpen = Boolean(navAnchorEl);
  useEffect(() => {
    if (!isPhoneLike) {
      setNavAnchorEl(null); 
    }
  },[isPhoneLike]);


  let storedUser: any = null;
  try {
    storedUser = JSON.parse(localStorage.getItem("user") || "null");
  } catch {
    storedUser = null;
  }

 const userRole = data?.me?.role?.toLowerCase() || storedUser?.role?.toLowerCase() || "";
 const hasCompletedOnboarding = data?.me?.isFirstLoggedIn === false || storedUser?.isFirstLoggedIn === false;

  const sidebarItems = [
    {
      text: "Dashboard",
      path: "/dashboard",
      icon: <LayoutDashboard size={24} />,
    },
    {
      text: "Billing Dashboard",
      path: "/billing",
      icon: <HandCoins size={24} />,
    },
    {
      text: "Reports Dashboard",
      path: "/reports",
      icon: <ChartNoAxesCombined size={24} />,
    },
    {
      text: "Offers",
      path: "/offers",
      icon: <CirclePercent size={24} />,
    },
    {
      text: "Instructors",
      path: "/instructors",
      icon: <Presentation size={24} />,
    },
    {
      text: "Admin",
      path: "/admin",
      icon: <GroupIcon />,
    },
    {
      text: "Classes",
      path: "/classes",
      icon: <HealthiconsITrainingClass width={30} height={30} />,
    },
    {
      text: "Calendar",
      path: "/calendar",
      icon: <ClipboardCheck size={24} />,
    },
    {
      text: "Students",
      path: "/students",
      icon: <HugeiconsStudents width={30} height={30} />,
    },
    {
      text: "Passes",
      path: "/passes",
      icon: <Ticket size={24} />,
    },
    {
      text: "Events",
      path: "/events",
      icon: <Calendar size={24} />,
    },
    {
      text: "Notifications",
      path: "/notifications",
      icon: <Bell size={24} />,
    },
    {
      text: "Settings",
      path: "/settings",
      icon: <Settings size={24} />,
    },
  ];
    const filteredSidebarItems = useMemo(() => {
      const role = userRole.toUpperCase();
      if (role === "INSTRUCTOR") {
        return sidebarItems.filter((item) =>
          ["Dashboard", "Classes", "Calendar", "Notifications", "Settings"].includes(item.text)
        );
      }

      if (role === "ADMIN") {
        return sidebarItems.filter(
          (item) => !["Billing Dashboard", "Reports Dashboard", "Admin"].includes(item.text)
        );
      }

      if (role === "USER") {
        return sidebarItems.filter((item) =>
          ["Dashboard", "Classes", "Passes", "Calendar", "Events", "Notifications", "Settings"].includes(item.text)
        );
      }
      return sidebarItems;
    }, [userRole, sidebarItems]);





  const displayName = hasCompletedOnboarding
  ? data?.me?.fullname || storedUser?.fullname || "Customer"
  : "Customer";
  const email = data?.me?.email || storedUser?.email || "customer@example.com";
  const photoValue = hasCompletedOnboarding
  ? data?.me?.uploadPhoto || "" : "";
  const photoSrc = photoValue
    ? photoValue.startsWith("data:")
      ? photoValue
      : `data:image/*;base64,${photoValue}`
    : "";

  const getInitial = (name: string, email: string) => {
    if (!name || name.toLowerCase() === "customer") {
      return email?.[0]?.toUpperCase() || "U";
    }
    return name[0].toUpperCase();
  };

  const userInitial = hasCompletedOnboarding ? getInitial(displayName, email) : "C";

  const handleLogoutConfirm = async () => {
    try {
      await apollo.clearStore();
    } finally {
      setLogoutDialogOpen(false);
      await logout();
      navigate("/");
    }
  };

  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const sidebarWidth = isTablet ? "200px" : "240px";

  return (
    <Box
      sx={{
        position: "absolute", 
        top: 0,
        left: { xs: 0, sm: sidebarWidth }, 
        right: 0,
        zIndex: (t) => t.zIndex.appBar,
        backgroundColor: "#fff",
        borderBottom: "1px solid rgba(0,0,0,0.06)",
        padding: 0,
        margin: 0,
        transition: "left 0.2s ease",
      }}
    >
      <Toolbar
        disableGutters
        sx={{
          height: 56,
          minHeight: 56,
          padding: 0,
          pl: isPhoneLike ? 0 : 3,
          pr: isPhoneLike ? 1 : 3,
          py: isPhoneLike ? 0 : 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box display="flex" alignItems="center">
          {isPhoneLike && (
            <IconButton
              onClick={(e) => setNavAnchorEl(e.currentTarget)}
              disableRipple
              sx={{
                backgroundColor: "#6CBC44",
                color: "#fff",
                width: 46,
                height: 55,
                padding: 0,
                borderRadius: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                "&:hover": {
                  backgroundColor: "#6CBC44",
                },
              }}
            >
              <MenuIcon sx={{ fontSize: 28 }} />
            </IconButton>
          )}
          <Box display="flex" alignItems="center">
            {isPhoneLike && (
              <Box
                component="img"
                src={logo}
                alt="United Grovees"
                sx={{
                  height: 20,
                  width: "auto",
                  ml: 2,
                  mr: 1,
                }}
              />
            )}
            {isPhoneLike && (
              <Typography
                fontWeight={600}
                fontFamily={themestyle.fontFamily.Roboto}
                sx={{
                  lineHeight: 1,
                  ml: 0,
                }}
              >
                United Grovees
              </Typography>
            )}
          </Box>
        </Box>
        <Box display="flex" alignItems="center" gap={{ xs: 0.5, sm: 2 }}>
          {userRole === "user" && (
            <>
              <IconButton
                onClick={() => setCartOpen(true)}
                sx={{
                  border: "1px solid #DDE6D5",
                  borderRadius: "14px",
                  width: isPhoneLike ? 36 : 44,
                  height: isPhoneLike ? 36 : 44,
                  "& svg": {
                    fontSize: isPhoneLike ? 20 : 24,
                  },
                }}
              >
                <Badge
                  badgeContent={totalCount}
                  color="error"
                  sx={{
                    "& .MuiBadge-badge": {
                      fontSize: 10,
                      height: 16,
                      minWidth: 16,
                      top: -2,
                      right: -2,
                    },
                  }}
                >
                  <ShoppingCartOutlinedIcon />
                </Badge>
              </IconButton>
              <CartDrawer open={isCartOpen} onClose={() => setCartOpen(false)} />
            </>
          )}
          <NotificationDropdown isPhoneLike={isPhoneLike} />
          {!isPhoneLike && (
            <Box
              sx={{
                width: "1px",
                height: isPhoneLike ? 24 : 32,
                backgroundColor: "#DDE6D5",
                mx: isPhoneLike ? 1 : 1.5,
              }}
            />
          )}
          {!isPhoneLike && (
            <Box textAlign="right">
              <Typography
                sx={{
                  fontSize: 12,
                  color: "#94A3B8",
                  lineHeight: 1,
                }}
              >
                HELLO!
              </Typography>
              <Typography
                sx={{
                  fontWeight: 600,
                  lineHeight: 1.2,
                }}
              >
                {displayName}
              </Typography>
            </Box>
          )}
          <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
            <Avatar
              src={photoSrc || undefined}
              sx={{
                bgcolor: "#6CBC44",
                color: "#fff",
                fontWeight: 700,
              }}
            >
              {userInitial}
            </Avatar>
          </IconButton>
        </Box>
      </Toolbar>
      <Menu
        anchorEl={navAnchorEl}
        open={navMenuOpen}
        transitionDuration={0}
        onClose={() => setNavAnchorEl(null)}
        PaperProps={{
          sx: {
            width: 240,
            maxWidth: "80vw",
            borderRadius: 2,
          },
        }}
      >
        {filteredSidebarItems.map((item) => (
          <MenuItem
            key={item.text}
            onClick={() => {
              navigate(item.path);
              setNavAnchorEl(null);
            }}
            sx={{ py: 1.5 }}
          >
            <ListItemIcon
              sx={{
                minWidth: 36,
                color: "#111",
                "& svg": { fontSize: 26, strokeWidth: 1.5 },
              }}
            >
              {item.icon}
            </ListItemIcon>
            <ListItemText
              primary={item.text}
              primaryTypographyProps={{ fontWeight: 500 }}
            />
          </MenuItem>
        ))}
      </Menu>
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={() => setAnchorEl(null)}
      >
        <Box px={2} py={1}>
          <Typography fontWeight={700}>{displayName}</Typography>
          <Typography variant="body2">{email}</Typography>
        </Box>
        <Divider />
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            setLogoutDialogOpen(true);
          }}
          sx={{
            borderRadius: 3,
            color: themestyle.colors.error,
            "& .MuiListItemIcon-root": {
              color: themestyle.colors.error,
            },
          }}
        >
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Logout"
            primaryTypographyProps={{ fontWeight: 500 }}
          />
        </MenuItem>
      </Menu>
      <ConfirmDialog
        open={logoutDialogOpen}
        icon={<LogoutIcon sx={{ fontSize: 32, color: "#6CBC44" }} />}
        message="Are you sure you want to logout?"
        confirmText="Logout"
        cancelText="Cancel"
        confirmButtonColor="#6CBC44" 
        iconBgColor="danger"  
        onConfirm={handleLogoutConfirm}
        onClose={() => setLogoutDialogOpen(false)}
      />
    </Box>
  );
};
export default Header;
